import React, { useState, useEffect } from "react";
import cogoToast from "cogo-toast";
import CustomModal from "../../../../components/CustomModal";
import {
  FacebookShareButton,
  TwitterShareButton,
  LinkedinShareButton,
  WhatsappShareButton,
  EmailShareButton,
  FacebookIcon,
  TwitterIcon,
  LinkedinIcon,
  WhatsappIcon,
  EmailIcon,
} from "react-share";

/**
 * ShareButton — reusable share button for feed cards, editorial articles,
 * leaderboard cards, and community posts.
 *
 * Per PRD §7.2: "Share button on editorial articles, leaderboard cards,
 * and Community posts that generates a shareable link or
 * WhatsApp-formatted snippet."
 *
 * Uses react-share (same as Job Details, Invite, My Jobs pages) inside
 * the project's CustomModal for cross-browser consistency.
 */
const ShareButton = ({ url, title = "Workbrook", text = "", style = {} }) => {
  const [showModal, setShowModal] = useState(false);
  const [copied, setCopied] = useState(false);

  // Build the share URL — same pattern as MyJobs/Invite pages
  const getBaseWebUrl = () => {
    if (process.env.REACT_APP_WEB_URL) return process.env.REACT_APP_WEB_URL;
    if (typeof window !== "undefined" && window.location?.origin) {
      return window.location.origin;
    }
    return "";
  };

  const shareUrl = url || `${getBaseWebUrl()}/league`;
  const shareText = text || title;
  const hashtags = ["workbrook"];

  // Reset copied state when modal closes
  useEffect(() => {
    if (!showModal) {
      setCopied(false);
    }
  }, [showModal]);

  const handleCopyLink = async () => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(shareUrl);
      } else {
        // Fallback for older browsers
        const ta = document.createElement("textarea");
        ta.value = shareUrl;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      setCopied(true);
      cogoToast.success("Link copied to clipboard", {
        position: "top-center",
        hideAfter: 3,
      });
      setTimeout(() => setCopied(false), 2000);
    } catch {
      cogoToast.error("Could not copy link", {
        position: "top-center",
        hideAfter: 3,
      });
    }
  };

  return (
    <div style={{ position: "relative", display: "inline-block", ...style }}>
      <button
        type="button"
        onClick={() => setShowModal(true)}
        style={btnStyle}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = "#f8f9fa";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = "transparent";
        }}
        title="Share"
      >
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <circle cx="18" cy="5" r="3" />
          <circle cx="6" cy="12" r="3" />
          <circle cx="18" cy="19" r="3" />
          <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
          <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
        </svg>
        <span>Share</span>
      </button>

      <CustomModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title="Share to Social Network"
        maxWidth="420px"
      >
        {/* Social share buttons — same react-share components as other pages */}
        <div style={{ textAlign: "center", padding: "8px 0 20px" }}>
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              gap: "12px",
              flexWrap: "wrap",
            }}
          >
            <FacebookShareButton
              url={shareUrl}
              quote={shareText}
              className="mr-2"
              hashtag="#workbrook"
            >
              <FacebookIcon size={40} round />
            </FacebookShareButton>

            <TwitterShareButton
              url={shareUrl}
              title={shareText}
              className="mr-2"
              hashtags={hashtags}
            >
              <TwitterIcon size={40} round />
            </TwitterShareButton>

            <LinkedinShareButton url={shareUrl} className="mr-2">
              <LinkedinIcon size={40} round />
            </LinkedinShareButton>

            <WhatsappShareButton
              url={`${shareText}  ${shareUrl}`}
              className="mr-2"
            >
              <WhatsappIcon size={40} round />
            </WhatsappShareButton>

            <EmailShareButton
              url={shareUrl}
              subject={title}
              body={shareText}
            >
              <EmailIcon size={40} round />
            </EmailShareButton>
          </div>
        </div>

        {/* Copy link section */}
        <div style={linkPreviewStyle}>
          <input
            type="text"
            value={shareUrl}
            readOnly
            style={linkInputStyle}
            onClick={(e) => e.target.select()}
          />
          <button
            type="button"
            onClick={handleCopyLink}
            style={{
              ...copyBtnStyle,
              backgroundColor: copied ? "#16a34a" : "#3a6ed4",
            }}
          >
            {copied ? "Copied!" : "Copy"}
          </button>
        </div>
      </CustomModal>
    </div>
  );
};

const btnStyle = {
  background: "none",
  border: "none",
  display: "flex",
  alignItems: "center",
  gap: "6px",
  cursor: "pointer",
  color: "#64748b",
  fontSize: "14px",
  fontWeight: "500",
  padding: "4px 8px",
  borderRadius: "6px",
  transition: "background-color 0.2s",
};

const linkPreviewStyle = {
  display: "flex",
  alignItems: "center",
  gap: "8px",
  backgroundColor: "#f1f5f9",
  borderRadius: "10px",
  padding: "4px 4px 4px 12px",
};

const linkInputStyle = {
  flex: 1,
  border: "none",
  background: "none",
  outline: "none",
  fontSize: "13px",
  color: "#64748b",
  padding: "8px 0",
  minWidth: 0,
};

const copyBtnStyle = {
  color: "#fff",
  border: "none",
  borderRadius: "8px",
  padding: "8px 16px",
  fontSize: "13px",
  fontWeight: "600",
  cursor: "pointer",
  flexShrink: 0,
  transition: "background-color 0.15s",
};

export default ShareButton;
